import { useEffect, useState, useRef } from "react";
import { Header } from "@/components/Header";
import { ReadinessMeter } from "@/components/ReadinessMeter";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Play, Square, Trophy, Clock, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import OperationalBlueprint from "@/components/OperationalBlueprint";

export default function RecruitDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const [showManual, setShowManual] = useState(false);
  const [manualMinutes, setManualMinutes] = useState("");

  const fetchData = async () => {
    try {
      const { data } = await api.get("/sessions/me");
      setData(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Resume running session from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("waa_session_start");
    if (saved) {
      const startedAt = Number(saved);
      startTimeRef.current = startedAt;
      setRunning(true);
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }
  }, []);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const startSession = () => {
    const now = Date.now();
    startTimeRef.current = now;
    localStorage.setItem("waa_session_start", String(now));
    setRunning(true);
    setElapsed(0);
    toast.success("Session started. Stay airborne.");
  };

  const stopAndLog = async () => {
    const minutes = elapsed / 60;
    if (elapsed < 60) {
      toast.error("Session too short — keep flying for at least 1 minute");
      setRunning(false);
      setElapsed(0);
      localStorage.removeItem("waa_session_start");
      return;
    }
    try {
      await api.post("/sessions/log", { minutes });
      toast.success(`Logged ${minutes.toFixed(2)} minutes of activation`);
      setRunning(false);
      setElapsed(0);
      localStorage.removeItem("waa_session_start");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log session");
    }
  };

  const logManual = async (e) => {
    e.preventDefault();
    const m = parseFloat(manualMinutes);
    if (!m || m <= 0) {
      toast.error("Enter a positive number of minutes");
      return;
    }
    try {
      await api.post("/sessions/log", { minutes: m });
      toast.success(`Logged ${m} minutes`);
      setManualMinutes("");
      setShowManual(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const fmt = (s) => {
    const h = String(Math.floor(s / 3600)).padStart(2, "0");
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    return `${h}:${m}:${sec}`;
  };

  return (
    <div className="min-h-screen bg-[#060B14]">
      <Header />

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Greeting */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="hud-line w-10" />
              <span className="hud-label">Recruiting Slayer · Pilot Dashboard</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl text-white font-light">
              Briefing for <span className="text-gold italic">{user?.full_name?.split(" ")[0]}</span>
            </h1>
            <p className="text-slate-400 text-sm mt-2">Week ascent in progress · Goal: 7 hours of Future Activation</p>
          </div>
          <Link to="/knowledge" data-testid="dashboard-knowledge-link" className="btn-ghost-gold px-5 py-2.5 rounded-sm text-sm">
            Knowledge Base ›
          </Link>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Readiness Meter */}
          <section className="lg:col-span-5 glass-strong rounded-sm p-8 flex flex-col items-center text-center">
            <div className="hud-label mb-6">Operational Readiness</div>
            <ReadinessMeter
              percent={data?.progress_pct || 0}
              hours={data?.week_hours || 0}
              goal={7}
            />
            <div className="mt-8 grid grid-cols-2 gap-4 w-full">
              <Stat label="This Week" value={`${(data?.week_hours || 0).toFixed(2)}h`} testId="stat-week-hours" />
              <Stat label="Streak" value={`${data?.streak_weeks || 0} wks`} testId="stat-streak" />
            </div>
          </section>

          {/* Activation Clock */}
          <section className="lg:col-span-7 glass-strong rounded-sm p-8 flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <div className="hud-label">Future Activation Clock</div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${running ? "bg-[#34D399] light-pulse glow-green" : "bg-slate-600"}`} />
                <span className="text-xs text-slate-400">{running ? "Active" : "Idle"}</span>
              </div>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center py-6">
              <div className="font-display font-light text-6xl sm:text-7xl text-white font-mono-aviation tracking-wider" data-testid="session-timer">
                {fmt(elapsed)}
              </div>
              <div className="text-xs text-slate-500 mt-3">Hours · Minutes · Seconds</div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {!running ? (
                <button
                  onClick={startSession}
                  data-testid="start-session-button"
                  className="btn-gold py-3.5 rounded-sm flex items-center justify-center gap-2"
                >
                  <Play size={16} /> Start Session
                </button>
              ) : (
                <button
                  onClick={stopAndLog}
                  data-testid="log-hours-button"
                  className="bg-[#F43F5E] hover:bg-[#FB7185] text-white py-3.5 rounded-sm flex items-center justify-center gap-2 transition-colors font-semibold"
                >
                  <Square size={16} /> Stop & Log Hours
                </button>
              )}
              <button
                onClick={() => setShowManual((s) => !s)}
                data-testid="manual-log-toggle"
                className="btn-ghost-gold py-3.5 rounded-sm flex items-center justify-center gap-2"
              >
                <Plus size={16} /> Log Manually
              </button>
            </div>

            {showManual && (
              <form onSubmit={logManual} className="mt-4 flex gap-2" data-testid="manual-log-form">
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={manualMinutes}
                  onChange={(e) => setManualMinutes(e.target.value)}
                  placeholder="Minutes"
                  data-testid="manual-minutes-input"
                  className="flex-1 bg-[#0A1122] border border-white/10 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-gold"
                />
                <button type="submit" data-testid="manual-log-submit" className="btn-gold px-6 rounded-sm">Add</button>
              </form>
            )}
          </section>

          {/* Consistent Creator Reward */}
          <section className="lg:col-span-7 glass rounded-sm p-8" data-testid="consistent-creator-card">
            <div className="flex items-start gap-5">
              <div className="p-3 border border-gold/40 rounded-sm">
                <Trophy size={22} className="text-gold" />
              </div>
              <div className="flex-1">
                <div className="hud-label mb-2">Consistent Creator Reward</div>
                <div className="font-display text-xl text-white mb-2">
                  Hit 7 hours for 4 weeks → <span className="text-gold">$5 off next month</span>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`flex-1 h-2 rounded-sm ${
                        (data?.streak_weeks || 0) > i ? "bg-gold glow-gold" : "bg-white/10"
                      }`}
                      data-testid={`streak-week-${i}`}
                    />
                  ))}
                </div>
                <div className="text-xs text-slate-500 mt-3 font-mono-aviation">
                  {data?.streak_weeks || 0} / 4 consecutive weeks
                </div>
              </div>
            </div>
          </section>

          {/* Recent sessions */}
          <section className="lg:col-span-5 glass rounded-sm p-8">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={14} className="text-gold" />
              <span className="hud-label">Recent Logs</span>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="recent-sessions-list">
              {data?.sessions?.slice(0, 6).map((s) => (
                <div key={s.id} className="flex items-center justify-between py-2 border-b border-white/5">
                  <div className="text-xs text-slate-400 font-mono-aviation">
                    {new Date(s.logged_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="text-sm text-white font-display">{(s.minutes / 60).toFixed(2)}h</div>
                </div>
              ))}
              {(!data?.sessions || data.sessions.length === 0) && (
                <div className="text-sm text-slate-500 py-6 text-center">No flights logged yet. Begin your first session.</div>
              )}
            </div>
          </section>
        </div>

        {/* Operational Blueprint */}
        <div className="mt-8">
          <OperationalBlueprint />
        </div>
      </main>
    </div>
  );
}

const Stat = ({ label, value, testId }) => (
  <div className="border border-white/10 rounded-sm p-4 text-left" data-testid={testId}>
    <div className="hud-label text-[9px] mb-1">{label}</div>
    <div className="font-display text-xl text-white">{value}</div>
  </div>
);
